(() => {
  if (customElements.get('zz-video-preview')) return;

  const visible = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        entry.target.inView =
          entry.isIntersecting &&
          entry.intersectionRect.width > 0 &&
          entry.intersectionRect.height > 0;
        entry.target.syncPreview();
      }
    },
    { threshold: [0, 0.01] }
  );
  const openDialogs = new Set();
  let previousOverflow;
  function lockScroll(block, lock) {
    if (lock) {
      if (openDialogs.has(block)) return;
      if (!openDialogs.size) {
        previousOverflow = document.documentElement.style.overflow;
        document.documentElement.style.overflow = 'hidden';
      }
      openDialogs.add(block);
    } else if (openDialogs.delete(block) && !openDialogs.size) {
      document.documentElement.style.overflow = previousOverflow;
    }
  }

  class VideoPreview extends HTMLElement {
    connectedCallback() {
      if (this.events) return;
      this.events = new AbortController();
      this.preview = this.querySelector('.small-preview-video');
      this.frame = this.querySelector('.zz-video-preview');
      this.dialog = this.querySelector('dialog');
      this.fullVideo = this.querySelector('.zz-preview-full-video');
      this.trigger = this.querySelector('.zz-video-preview-btn');
      this.mobile = matchMedia('(max-width: 1023px)');
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
      this.positions = new Map();
      this.inView = false;
      this.pageHidden = false;
      this.listen(this.trigger, 'click', () => this.open());
      this.listen(this.querySelector('.zz-preview-close'), 'click', () =>
        this.close()
      );
      this.listen(this.dialog, 'cancel', (event) => {
        event.preventDefault();
        this.close();
      });
      this.listen(this.dialog, 'close', () => {
        if (!this.dialog.open) this.onClose();
      });
      this.listen(this.dialog, 'pointerdown', (event) => {
        this.backdropDown = event.target === this.dialog;
      });
      this.listen(this.dialog, 'click', (event) => {
        if (this.backdropDown && event.target === this.dialog) this.close();
      });
      this.listen(this.mobile, 'change', () => {
        this.syncPreview();
        if (this.dialog.open) this.playFull();
      });
      this.listen(this.reduced, 'change', () => this.syncPreview());
      if (navigator.connection?.addEventListener)
        this.listen(navigator.connection, 'change', () => this.syncPreview());
      this.listen(document, 'visibilitychange', () => {
        this.syncPreview();
        if (document.hidden) this.fullVideo?.pause();
      });
      this.listen(window, 'pagehide', () => {
        this.pageHidden = true;
        this.preview?.pause();
        this.fullVideo?.pause();
        this.cancelPending?.();
      });
      this.listen(window, 'pageshow', () => {
        this.pageHidden = false;
        this.syncPreview();
      });
      if (this.fullVideo) {
        this.listen(this.fullVideo, 'playing', () => {
          if (!this.dialog.open || document.hidden || this.pageHidden)
            this.fullVideo.pause();
        });
        this.listen(this.fullVideo, 'loadedmetadata', () => {
          const position = this.positions.get(
            this.fullVideo.getAttribute('src')
          );
          if (position > 0 && position < this.fullVideo.duration)
            this.fullVideo.currentTime = position;
        });
      }
      if (this.preview) {
        this.listen(this.preview, 'playing', () => {
          if (!this.canPreview()) {
            this.preview.pause();
            return;
          }
          this.frame.classList.add('is-playing');
        });
        this.listen(this.preview, 'error', () => {
          this.failedSource = this.preview.getAttribute('src');
          this.frame.classList.remove('is-playing');
        });
        // Observe only containing slides, so hidden/fade slides cannot autoplay.
        this.slides = [];
        this.slideObserver = new MutationObserver(() => this.syncPreview());
        for (let el = this.parentElement; el; el = el.parentElement) {
          if (
            el.matches('.swiper-slide') ||
            el.parentElement?.matches('.swiper-wrapper, .zz-swiper-wrapper')
          ) {
            this.slides.push(el);
            this.slideObserver.observe(el, {
              attributes: true,
              attributeFilter: ['class', 'aria-hidden'],
            });
          }
        }
        visible.observe(this);
      }
    }

    listen(target, type, listener) {
      target.addEventListener(type, listener, { signal: this.events.signal });
    }

    source(video) {
      return this.mobile.matches ? video.dataset.mbSrc : video.dataset.pcSrc;
    }

    canPreview() {
      return (
        this.isConnected &&
        this.events &&
        this.inView &&
        !this.pageHidden &&
        !document.hidden &&
        !this.dialog.open &&
        !this.reduced.matches &&
        !navigator.connection?.saveData &&
        this.slides.every((slide) => {
          const swiper = slide.parentElement?.parentElement?.swiper;
          return (
            slide.getAttribute('aria-hidden') !== 'true' &&
            (!swiper ||
              swiper.destroyed ||
              slide.classList.contains('swiper-slide-active'))
          );
        })
      );
    }

    syncPreview() {
      this.cancelPending?.();
      if (!this.preview) return;
      const src = this.source(this.preview);
      if (!this.canPreview() || !src || src === this.failedSource) {
        this.preview.pause();
        if (src === this.failedSource)
          this.frame.classList.remove('is-playing');
        return;
      }
      const poster = this.querySelector('.zz-preview-poster img');
      let timeout;
      const cancel = () => {
        clearTimeout(timeout);
        poster?.removeEventListener('load', schedule);
        poster?.removeEventListener('error', schedule);
        this.cancelPending = null;
      };
      const play = () => {
        cancel();
        if (!this.canPreview() || src !== this.source(this.preview)) return;
        if (this.preview.getAttribute('src') !== src) {
          this.preview.pause();
          this.frame.classList.remove('is-playing');
          this.preview.src = src;
        }
        this.preview.muted = true;
        this.preview.play().catch(() => {
          /* Static poster remains on autoplay denial. */
        });
      };
      const schedule = () => {
        cancel();
        this.cancelPending = cancel;
        timeout = setTimeout(play, 0);
      };
      this.cancelPending = cancel;
      if (
        !poster ||
        poster.complete ||
        this.preview.getAttribute('src') === src
      )
        schedule();
      else {
        poster.addEventListener('load', schedule, { once: true });
        poster.addEventListener('error', schedule, { once: true });
        timeout = setTimeout(schedule, 1500);
      }
    }

    open() {
      if (!this.fullVideo || this.dialog.open) return;
      this.dialog.showModal();
      this.opened = true;
      lockScroll(this, true);
      this.syncPreview();
      // Keep play in the click gesture for audible playback on iOS/Safari.
      this.playFull();
    }

    playFull() {
      const video = this.fullVideo;
      const src = this.source(video);
      if (!src || document.hidden || this.pageHidden) return;
      if (video.getAttribute('src') !== src) {
        this.rememberPosition();
        video.pause();
        const poster = this.mobile.matches
          ? video.dataset.mbPoster
          : video.dataset.pcPoster;
        if (poster) video.poster = poster;
        else video.removeAttribute('poster');
        video.src = src;
      }
      video.play().catch(() => {
        /* Native controls allow an explicit retry. */
      });
    }

    rememberPosition() {
      const src = this.fullVideo?.getAttribute('src');
      if (src) this.positions.set(src, this.fullVideo.currentTime);
    }

    release(video) {
      if (!video) return;
      video.pause();
      video.removeAttribute('src');
      video.removeAttribute('poster');
      video.load();
    }

    close() {
      if (!this.dialog.open) return;
      this.dialog.close();
      this.onClose();
    }

    onClose() {
      if (!this.opened) return;
      this.opened = false;
      this.rememberPosition();
      this.release(this.fullVideo);
      lockScroll(this, false);
      this.syncPreview();
    }

    disconnectedCallback() {
      this.events?.abort();
      this.events = null;
      this.cancelPending?.();
      visible.unobserve(this);
      this.slideObserver?.disconnect();
      if (this.dialog?.open) this.dialog.close();
      this.opened = false;
      lockScroll(this, false);
      this.release(this.preview);
      this.release(this.fullVideo);
      this.frame?.classList.remove('is-playing');
      this.failedSource = null;
    }
  }
  customElements.define('zz-video-preview', VideoPreview);
})();
